import { useState, useEffect } from 'react';
import { EventBusProps, eventBusInstance, EventKeys } from '@moodys/cre-cpm.models.events';

export interface UseEventPublishedOptions {
  key: EventKeys
  maxRetries?: number
  retryEvery?: number
  minSubscribers?: number
  eventBus?: EventBusProps
}

export const useEventPublisher = <T>(
  {
    key,
    maxRetries = 10,
    retryEvery = 500,
    minSubscribers = 0,
    eventBus = eventBusInstance,
  }: UseEventPublishedOptions,
  initialState?: T,
): [
    (value: T) => void,
  ] => {
  const [retry, setRetry] = useState<number>(0);
  const [hasEnoughSubscribers, setHasEnoughSubscribers] = useState<boolean>(minSubscribers === 0);
  const [messages, setMessage] = useState<T | undefined>(initialState);

  useEffect(() => {
    if (eventBus.countSubscriptions(key) >= minSubscribers) {
      setHasEnoughSubscribers(true);
    } else {
      setTimeout(() => {
        if (retry < maxRetries) {
          setRetry(retry + 1);
        } else {
          setHasEnoughSubscribers(true);
        }
      }, retryEvery);
    }
  }, [retry]);

  useEffect(() => {
    if (hasEnoughSubscribers && messages) {
      eventBus.publish(key, messages);
      setMessage(undefined);
    }
  }, [hasEnoughSubscribers]);

  useEffect(() => {
    if (hasEnoughSubscribers && messages) {
      eventBus.publish(key, messages);
      setMessage(undefined);
    }
  });

  const sendMessage = (value: T) => {
    if (hasEnoughSubscribers) {
      eventBus.publish(key, value);
    } else {
      setMessage(value);
    }
  };
  return [sendMessage];
};
